import { useState, useEffect, useMemo, useRef } from "react";
import Map, { useMap } from "react-map-gl/maplibre";
import "maplibre-gl/dist/maplibre-gl.css";
import maplibregl from "maplibre-gl";
import * as pmtiles from "pmtiles";

import PurpleLine from "./components/PurpleLine";
import GreenLine from "./components/GreenLine";
import YellowLine from "./components/YellowLine";
import ActionPanel from "./components/ActionPanel";
import About from "./components/About";
import LoadingOverlay from "./components/LoadingOverlay";
import Footer from "./components/Footer";
import Header from "./components/Header";
import TransportToggle from "./components/TransportToggle";

const CITY_BBOX = [77.35, 12.8, 77.8, 13.15];
const padBbox = (bbox, padDeg = 0.05) => [
  [bbox[0] - padDeg, bbox[1] - padDeg],
  [bbox[2] + padDeg, bbox[3] + padDeg],
];

function buildStyle(theme) {
  const isDark = theme === "dark";
  const C = isDark
    ? {
      bg: "#0b0e12",
      boundary: "#3a3f47",
      land: "#0f141a",
      water: "#133042",
      roadCasing: "#0b0e12",
      road: "#3b4857",
      text: "#dbe3ee",
      textHalo: "#0b0e12",
    }
    : {
      bg: "#f0f0e4ff",
      boundary: "#a0a0a0",
      land: "#9dea9285",
      water: "#74dae7",
      roadCasing: "#ffffff",
      road: "#cbd0d695",
      text: "#4b5563",
      textHalo: "#ffffff",
    };

  if (!window._pmtilesRegistered) {
    const protocol = new pmtiles.Protocol();
    maplibregl.addProtocol("pmtiles", protocol.tile);
    window._pmtilesRegistered = true;
  }
  const PMTILES_URL =
    process.env.REACT_APP_PMTILES_URL ||
    "https://storage.googleapis.com/tiles.nammametrolive.com/bangalore.pmtiles";

  return {
    version: 8,
    glyphs: "https://protomaps.github.io/basemaps-assets/fonts/{fontstack}/{range}.pbf",
    metadata: { "mapbox:default-font": ["Noto Sans Regular"] },
    transition: { duration: 300, delay: 0 },
    sources: {
      bangalore: {
        type: "vector",
        url: `pmtiles://${PMTILES_URL}`,
        minzoom: 0,
        maxzoom: 14,
        attribution: "© OpenStreetMap contributors",
      },
      city_mask: {
        type: "geojson",
        data: {
          type: "Feature",
          geometry: {
            type: "Polygon",
            coordinates: [
              [
                [-180, -85],
                [180, -85],
                [180, 85],
                [-180, 85],
                [-180, -85],
              ],
              [
                [CITY_BBOX[0], CITY_BBOX[1]],
                [CITY_BBOX[2], CITY_BBOX[1]],
                [CITY_BBOX[2], CITY_BBOX[3]],
                [CITY_BBOX[0], CITY_BBOX[3]],
                [CITY_BBOX[0], CITY_BBOX[1]],
              ],
            ],
          },
        },
      },
    },
    layers: [
      { id: "background", type: "background", paint: { "background-color": C.bg } },
      {
        id: "boundaries",
        type: "line",
        source: "bangalore",
        "source-layer": "boundaries",
        paint: { "line-color": C.boundary, "line-width": 0.8, "line-opacity": 0.6 },
      },
      {
        id: "landuse",
        type: "fill",
        source: "bangalore",
        "source-layer": "landuse",
        paint: { "fill-color": C.land, "fill-opacity": isDark ? 0.6 : 0.7 },
      },
      {
        id: "water",
        type: "fill",
        source: "bangalore",
        "source-layer": "water",
        paint: { "fill-color": C.water, "fill-opacity": isDark ? 0.85 : 0.8 },
      },
      {
        id: "roads-major-casing",
        type: "line",
        source: "bangalore",
        "source-layer": "roads",
        filter: [
          "any",
          ["==", ["get", "kind"], "highway"],
          ["==", ["get", "kind"], "major_road"],
          ["in", ["get", "kind_detail"], ["literal", ["motorway", "trunk", "primary"]]],
        ],
        paint: {
          "line-color": C.roadCasing,
          "line-opacity": 0.9,
          "line-width": [
            "interpolate",
            ["linear"],
            ["zoom"],
            8,
            1.6,
            12,
            3.8,
            14,
            6.0,
          ],
        },
      },
      {
        id: "roads-major",
        type: "line",
        source: "bangalore",
        "source-layer": "roads",
        filter: [
          "any",
          ["==", ["get", "kind"], "highway"],
          ["==", ["get", "kind"], "major_road"],
          ["in", ["get", "kind_detail"], ["literal", ["motorway", "trunk", "primary"]]],
        ],
        paint: {
          "line-color": C.road,
          "line-opacity": isDark ? 0.9 : 0.95,
          "line-width": [
            "interpolate",
            ["linear"],
            ["zoom"],
            8,
            1.2,
            12,
            3.0,
            14,
            4.8,
          ],
        },
      },
      {
        id: "outside-dim",
        type: "fill",
        source: "city_mask",
        paint: {
          "fill-color": isDark ? "#0b0e12" : "#ffffff",
          "fill-opacity": isDark ? 0.35 : 0.25,
        },
      },
      {
        id: "place-major",
        type: "symbol",
        source: "bangalore",
        "source-layer": "places",
        minzoom: 12.7,
        filter: [
          "any",
          ["in", ["get", "place"], ["literal", ["city", "town"]]],
          ["in", ["get", "class"], ["literal", ["city", "town"]]],
          ["in", ["get", "kind"], ["literal", ["city", "town"]]],
          [">=", ["coalesce", ["get", "population"], ["get", "pop"], ["get", "pop_max"], 0], 500000],
          ["<=", ["coalesce", ["get", "rank"], ["get", "scalerank"], 999], 2],
          true,
        ],
        layout: {
          "text-field": [
            "coalesce",
            ["get", "name:en"],
            ["get", "pgf:name:en"],
            ["get", "name_en"],
            ["get", "NAME_EN"],
            ["get", "name"],
          ],
          "text-size": ["interpolate", ["linear"], ["zoom"], 0, 10, 12, 10, 16, 10],
          "text-font": ["Noto Sans Regular"],
          "text-max-width": 8,
          "text-padding": 8,
        },
        paint: {
          "text-color": C.text,
          "text-halo-color": C.textHalo,
          "text-halo-width": 1.2,
        },
      },
    ],
  };
}

function MetroLines({ theme, filters, language }) {
  const { current } = useMap();
  const map = current?.getMap();
  if (!map) return null;
  return (
    <>
      {filters.yellow && (
        <YellowLine key={theme + "-yellow"} map={map} theme={theme} language={language} />
      )}
      {filters.purple && (
        <PurpleLine key={theme + "-purple"} map={map} theme={theme} language={language} />
      )}
      {filters.green && (
        <GreenLine key={theme + "-green"} map={map} theme={theme} language={language} />
      )}
    </>
  );
}

export default function App() {
  const mapRef = useRef(null);
  const didIntroRef = useRef(false);
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 2500);
    return () => clearTimeout(timer);
  }, []);

  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  const [filters, setFilters] = useState(() => {
    const saved = localStorage.getItem("filters");
    return saved
      ? JSON.parse(saved)
      : { yellow: true, purple: false, green: false };
  });

  useEffect(() => {
    localStorage.setItem("filters", JSON.stringify(filters));
  }, [filters]);

  const handleToggleLine = (color) => {
    setFilters((prev) => ({ ...prev, [color]: !prev[color] }));
  };

  const handleToggleAll = () => {
    const allSelected = Object.values(filters).every(Boolean);
    setFilters({
      yellow: !allSelected,
      purple: !allSelected,
      green: !allSelected,
    });
  };
  const [theme, setTheme] = useState(() => localStorage.getItem("theme") || "light");
  const [language, setLanguage] = useState(() => localStorage.getItem("language") || "en");
  const mapCenter = useMemo(() => [77.6169, 12.8648], []);
  const mapStyle = useMemo(() => buildStyle(theme), [theme]);
  const [showAbout, setShowAbout] = useState(false);
  const [isFavOpen, setIsFavOpen] = useState(false);
  const [showFavPanel, setShowFavPanel] = useState(false);
  const handleLanguageToggle = (code) => {
    setLanguage(code);
    localStorage.setItem("language", code); // persist choice
  };

  useEffect(() => localStorage.setItem("theme", theme), [theme]);
  useEffect(() => localStorage.setItem("language", language), [language]);
  useEffect(() => {
    try {
      localStorage.setItem("filters", JSON.stringify(filters));
    } catch { }
  }, [filters]);

  useEffect(() => {
    const selectedKeys = Object.keys(filters).filter((k) => filters[k]);
    if (selectedKeys.length === 0) return;
    setFilters((f) => {
      const next = { ...f };
      selectedKeys.forEach((k) => (next[k] = false));
      return next;
    });
    const id = setTimeout(() => {
      setFilters((f) => {
        const next = { ...f };
        selectedKeys.forEach((k) => (next[k] = true));
        return next;
      });
    }, 100);
    return () => clearTimeout(id);
  }, [theme]);

  useEffect(() => {
    const handleResize = () => {
      const desktop = window.innerWidth >= 1024;
      setIsDesktop(desktop);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (isLoading) return; // wait until loading overlay is gone
    const map = mapRef.current?.getMap();
    if (!map || didIntroRef.current) return;

    didIntroRef.current = true;

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const targetCenter = mapCenter;
    const targetZoom = isDesktop ? 12 : 11;
    const targetPitch = 0;
    const targetBearing = 0;

    if (reduceMotion) {
      map.jumpTo({
        center: targetCenter,
        zoom: targetZoom,
        pitch: targetPitch,
        bearing: targetBearing,
      });
      return;
    }

    // Start the fly-in animation once loading screen hides
    map.flyTo({
      center: targetCenter,
      zoom: targetZoom,
      pitch: targetPitch,
      bearing: targetBearing,
      duration: 2200,
      curve: 1.6,
      easing: (t) => 1 - Math.pow(1 - t, 3),
      essential: true,
    });
  }, [isLoading, isDesktop, mapCenter]);


  const toggleLine = (line) => setFilters((prev) => ({ ...prev, [line]: !prev[line] }));
  const areAllSelected = filters.purple && filters.yellow && filters.green;
  const toggleShowAll = () =>
    setFilters(
      areAllSelected
        ? { purple: false, yellow: false, green: false }
        : { purple: true, yellow: true, green: true }
    );

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        width: "100vw",
        background: theme === "dark" ? "#0b0e12" : "#ffffff",
        overflow: "hidden",
      }}
    >
      {isLoading && <LoadingOverlay setIsLoading={setIsLoading} />}

      {/* Map area shrinks slightly to make space for footer */}
      <div style={{ flex: "1 1 auto", position: "relative" }}>

        <Header
          theme={theme}
          language={language}
          onFavClick={() => setShowFavPanel(true)}
          onAboutClick={() => setShowAbout(true)}
          mapInstance={mapRef.current?.getMap()}
          onThemeToggle={() => setTheme((prev) => (prev === "dark" ? "light" : "dark"))}
          onLanguageToggle={handleLanguageToggle}
        />

        <TransportToggle
          theme={theme}
          language={language}
        />

        <Map
          ref={mapRef}
          initialViewState={{
            longitude: mapCenter[0],
            latitude: mapCenter[1],
            zoom: 3,
            pitch: 50,
            bearing: 20,
          }}
          style={{ width: "100%", height: "100%" }}
          mapStyle={mapStyle}
          attributionControl={{ position: "top-right", compact: false }}
          preserveDrawingBuffer={false}
          maxZoom={16}
          minZoom={2}
          dragRotate={true}
          touchZoomRotate={true}
          pitchWithRotate={true}
          maxBounds={padBbox(CITY_BBOX, 0.05)}
          glOptions={{ antialias: true }}
          pixelRatio={window.devicePixelRatio || 1}
          onLoad={(e) => {
            const map = e.target;
            map.resize();
          }}
        >
          <MetroLines key={theme} theme={theme} filters={filters} language={language} />
        </Map>

        <ActionPanel
          theme={theme}
          language={language}
          filters={filters}
          onToggleLine={handleToggleLine}
          areAllSelected={Object.values(filters).every(Boolean)}
          onToggleAll={handleToggleAll}
          onThemeToggle={() =>
            setTheme((prev) => (prev === "dark" ? "light" : "dark"))
          }
          onLanguageToggle={handleLanguageToggle}
          isFavOpen={showFavPanel}
          onCloseFav={() => setShowFavPanel(false)}
        />

        <About
          isOpen={showAbout}
          onClose={() => setShowAbout(false)}
          theme={theme}
          language={language}
        />
      </div>

      {/* Footer stays visible, no scroll */}
      <Footer theme={theme} language={language} />
    </div>
  );
}
